import { useCallback, useMemo, useState } from "react";
import { Cart } from "../../../domain";
import useCart from "../../../hooks/useCart";
import { useNotification } from "../../../services/Notifications";
import { getParameter, localStorageUtils } from "../../../util";
import {
  buildAccessoryCategories,
  categorizeProduct,
  sortProducts,
} from "./utils.js";
import AccessoriesHero from "./components/AccessoriesHero.jsx";
import AccessoriesToolbar from "./components/AccessoriesToolbar.jsx";
import AccessoriesCatalog from "./components/AccessoriesCatalog.jsx";
import WhyUsStrip from "./components/WhyUsStrip.jsx";
import AccessoriesToast from "./components/AccessoriesToast.jsx";
import s from "./AccessoriesPage.module.css";

export default function AccessoriesPage({
  route,
  influencer,
  shopProducts,
  isLoading,
}) {
  const [activeCategory, setActiveCategory] = useState("all");
  const [sortBy, setSortBy] = useState("curated");
  const { refresh } = useCart();
  const notify = useNotification();

  const allProducts = shopProducts?.allProducts || [];

  const categories = useMemo(
    () => buildAccessoryCategories(allProducts),
    [allProducts],
  );

  const filteredProducts = useMemo(() => {
    let products = allProducts;
    if (activeCategory !== "all") {
      products = products.filter(
        (p) => categorizeProduct(p) === activeCategory,
      );
    }
    return sortProducts(products, sortBy);
  }, [allProducts, activeCategory, sortBy]);

  const handleAddToCart = useCallback(
    async (product) => {
      const influencerId = influencer?.influencerId ?? product.influencer_id;
      const trackingCode =
        getParameter("stc") || localStorageUtils.getWithExpiry("stc");

      await Promise.all([
        Cart.addProduct({
          productId: product.product_id,
          influencerId,
          personalization: {},
          addons: [],
        }),
        Cart.update({
          influencerId,
          trackingCode,
        }),
      ]);

      await refresh();

      notify({
        message: `${product.name} added to cart`,
        Component: AccessoriesToast,
        duration: 2500,
      });
    },
    [influencer, refresh, notify],
  );

  return (
    <div className={s.page}>
      <AccessoriesHero products={allProducts} isLoading={isLoading} />
      <AccessoriesToolbar
        categories={categories}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
        sortBy={sortBy}
        onSortChange={setSortBy}
      />
      <AccessoriesCatalog
        products={filteredProducts}
        totalCount={allProducts.length}
        onAddToCart={handleAddToCart}
        isLoading={isLoading}
      />
      <WhyUsStrip />
    </div>
  );
}
